/**
 * 安全区域检测工具
 * 用于检测和处理刘海屏、钉钉环境等的安全区域
 */

export interface SafeAreaInsets {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

export interface DeviceInfo {
    isIOS: boolean;
    isAndroid: boolean;
    isDingTalk: boolean;
    hasNotch: boolean;
    screenWidth: number;
    screenHeight: number;
    devicePixelRatio: number;
}

/**
 * 安全区域检测器类
 */
export class SafeAreaDetector {
    private static instance: SafeAreaDetector;
    private deviceInfo: DeviceInfo;
    private safeAreaInsets: SafeAreaInsets;
    private listeners: Array<(insets: SafeAreaInsets) => void> = [];

    private constructor() {
        this.deviceInfo = this.detectDevice();
        this.safeAreaInsets = this.detectSafeAreaInsets();
        this.setupListeners();
    }

    public static getInstance(): SafeAreaDetector {
        if (!SafeAreaDetector.instance) {
            SafeAreaDetector.instance = new SafeAreaDetector();
        }
        return SafeAreaDetector.instance;
    }

    /**
     * 检测设备信息
     */
    private detectDevice(): DeviceInfo {
        const userAgent = navigator.userAgent.toLowerCase();
        const isIOS = /iphone|ipad|ipod/.test(userAgent);
        const isAndroid = /android/.test(userAgent);
        const isDingTalk = userAgent.includes('dingtalk') || userAgent.includes('aliapp');

        // 检测是否有刘海屏
        const hasNotch = this.detectNotch();

        return {
            isIOS,
            isAndroid,
            isDingTalk,
            hasNotch,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            devicePixelRatio: window.devicePixelRatio || 1
        };
    }

    /**
     * 检测是否有刘海屏
     */
    private detectNotch(): boolean {
        // 检查CSS环境变量
        if (CSS.supports('padding-top', 'env(safe-area-inset-top)')) {
            const testElement = document.createElement('div');
            testElement.style.paddingTop = 'env(safe-area-inset-top)';
            document.body.appendChild(testElement);

            const computedStyle = getComputedStyle(testElement);
            const topInset = parseInt(computedStyle.paddingTop) || 0;

            document.body.removeChild(testElement);

            return topInset > 0;
        }

        // 基于屏幕尺寸的启发式检测（针对已知的刘海屏设备）
        const { screenWidth, screenHeight, devicePixelRatio } = this.deviceInfo || {
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            devicePixelRatio: window.devicePixelRatio || 1
        };

        const physicalWidth = screenWidth * devicePixelRatio;
        const physicalHeight = screenHeight * devicePixelRatio;

        // iPhone X 系列及以上的刘海屏设备
        const notchDevices = [
            { width: 1125, height: 2436 }, // iPhone X, XS
            { width: 828, height: 1792 },  // iPhone XR
            { width: 1242, height: 2688 }, // iPhone XS Max
            { width: 1170, height: 2532 }, // iPhone 12, 12 Pro
            { width: 1284, height: 2778 }, // iPhone 12 Pro Max
            { width: 1080, height: 2340 }, // iPhone 12 mini
            { width: 1179, height: 2556 }, // iPhone 14 Pro
            { width: 1290, height: 2796 }, // iPhone 14 Pro Max
        ];

        return notchDevices.some(device =>
            (physicalWidth === device.width && physicalHeight === device.height) ||
            (physicalWidth === device.height && physicalHeight === device.width)
        );
    }

    /**
     * 检测安全区域插入值
     */
    private detectSafeAreaInsets(): SafeAreaInsets {
        const insets: SafeAreaInsets = { top: 0, bottom: 0, left: 0, right: 0 };

        // 尝试从CSS环境变量获取
        if (CSS.supports('padding-top', 'env(safe-area-inset-top)')) {
            const testElement = document.createElement('div');
            testElement.style.position = 'fixed';
            testElement.style.top = '0';
            testElement.style.left = '0';
            testElement.style.width = '1px';
            testElement.style.height = '1px';
            testElement.style.paddingTop = 'env(safe-area-inset-top)';
            testElement.style.paddingBottom = 'env(safe-area-inset-bottom)';
            testElement.style.paddingLeft = 'env(safe-area-inset-left)';
            testElement.style.paddingRight = 'env(safe-area-inset-right)';
            testElement.style.visibility = 'hidden';

            document.body.appendChild(testElement);

            const computedStyle = getComputedStyle(testElement);
            insets.top = parseInt(computedStyle.paddingTop) || 0;
            insets.bottom = parseInt(computedStyle.paddingBottom) || 0;
            insets.left = parseInt(computedStyle.paddingLeft) || 0;
            insets.right = parseInt(computedStyle.paddingRight) || 0;

            document.body.removeChild(testElement);
        }

        // 如果检测到刘海屏但没有获取到安全区域值，使用默认值
        if (this.deviceInfo.hasNotch && insets.top === 0) {
            if (this.deviceInfo.isIOS) {
                insets.top = 20; // 减少默认的状态栏高度，避免过度间距
                if (this.deviceInfo.isDingTalk) {
                    insets.top += 5; // 减少钉钉环境额外间距
                }
            }
        }

        return insets;
    }

    /**
     * 设置监听器
     */
    private setupListeners(): void {
        const updateInsets = () => {
            const newInsets = this.detectSafeAreaInsets();
            if (this.hasInsetsChanged(newInsets)) {
                this.safeAreaInsets = newInsets;
                this.notifyListeners();
            }
        };

        window.addEventListener('orientationchange', () => {
            setTimeout(updateInsets, 100);
        });

        window.addEventListener('resize', updateInsets);
    }

    /**
     * 检查安全区域是否发生变化
     */
    private hasInsetsChanged(newInsets: SafeAreaInsets): boolean {
        return (
            newInsets.top !== this.safeAreaInsets.top ||
            newInsets.bottom !== this.safeAreaInsets.bottom ||
            newInsets.left !== this.safeAreaInsets.left ||
            newInsets.right !== this.safeAreaInsets.right
        );
    }

    /**
     * 通知所有监听器
     */
    private notifyListeners(): void {
        this.listeners.forEach(listener => listener(this.safeAreaInsets));
    }

    /**
     * 获取当前安全区域插入值
     */
    public getSafeAreaInsets(): SafeAreaInsets {
        return { ...this.safeAreaInsets };
    }

    /**
     * 获取设备信息
     */
    public getDeviceInfo(): DeviceInfo {
        return { ...this.deviceInfo };
    }

    /**
     * 添加安全区域变化监听器
     */
    public addListener(listener: (insets: SafeAreaInsets) => void): void {
        this.listeners.push(listener);
    }

    /**
     * 移除安全区域变化监听器
     */
    public removeListener(listener: (insets: SafeAreaInsets) => void): void {
        const index = this.listeners.indexOf(listener);
        if (index > -1) {
            this.listeners.splice(index, 1);
        }
    }

    /**
     * 获取推荐的顶部安全间距
     */
    public getRecommendedTopPadding(): number {
        const { top } = this.safeAreaInsets;
        const { isDingTalk, hasNotch } = this.deviceInfo;

        // 优化：减少额外间距，避免页面整体高度过高
        if (isDingTalk && hasNotch) {
            return Math.max(top, 10); // 减少钉钉环境的额外间距
        }

        return Math.max(top, hasNotch ? 10 : 0); // 减少默认的最小间距
    }

    /**
     * 生成安全区域CSS样式
     */
    public generateSafeAreaCSS(options: {
        top?: boolean;
        bottom?: boolean;
        left?: boolean;
        right?: boolean;
        extraTop?: number;
    } = {}): React.CSSProperties {
        const { top = true, bottom = false, left = false, right = false, extraTop = 0 } = options;
        const style: React.CSSProperties = {};

        if (top) {
            const topPadding = this.getRecommendedTopPadding() + extraTop;
            style.paddingTop = `max(env(safe-area-inset-top), ${topPadding}px)`;
        }

        if (bottom) {
            style.paddingBottom = 'env(safe-area-inset-bottom)';
        }

        if (left) {
            style.paddingLeft = 'env(safe-area-inset-left)';
        }

        if (right) {
            style.paddingRight = 'env(safe-area-inset-right)';
        }

        return style;
    }
}

// 导出单例实例
export const safeAreaDetector = SafeAreaDetector.getInstance();
